import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { cloneDeep } from "lodash";
import { ConfigurationLayerModel } from "../models/configuration";
import { FilterModel } from '../models/filter';
import { ColorOfType, ColorSettings, LayerColor, LayerVisibility, UserLayer, UserSettings, UserSettingsDTO } from "../models/UserSettings";
import { EndpointsService } from "./endpoints.service";
import { UtilsService } from "./utils.service";
import { Layer } from "ol/layer";
import {FeatureType} from "../models/EnumTypes";

@Injectable({
  providedIn: 'root'
})
export class UserSettingsService{

  userSettings: UserSettings;
  updateSettings:Function;
  static default_transparency_sub_layers = {'Redundancy': 0.7,'NSE': 0.7,
                                      'Subtended Angles': 0.7, 'Available Pairs':0.7,
                                      "Cumulative Coverage": 0.7, 'Signal Delay': 0.7}
  static defaultLineColor = "#ff0000";
  static defaultLineWidth = 1;
  static defaultFillColor = "#ff0000";
  static defaultTransparency = 0.7;
  static defaultStaticLayerLineOpacity = 1;
  static defaultStaticLayerFillOpacity = 0.5;
  constructor(public http: HttpClient,
  public endpointService: EndpointsService,
  ){}

    updateUserSettingsFields(sourceUserSettings:UserSettings){
      let targetUserSettings = new UserSettings();
      let targetFlattened = this.flattenObject(targetUserSettings);
      let sourceFlattened = this.flattenObject(sourceUserSettings);
      let updatedTarget = this.updateTargetFields(targetFlattened,sourceFlattened);
      this.userSettings = this.unflattenObject(updatedTarget) as UserSettings;
      this.updateSettings();
    }
    getVisibilityObjectOfLayer(layerName:string){
      switch(layerName){
        case 'Navaids': return this.userSettings.navaidSettings.navaidVisibility;
        case 'Coordinate Points': return this.userSettings.coordinatePointSettings.coordinatePointVisibility
        case 'Procedures': return this.userSettings.procedureSettings.procedureVisibility;
        case 'Trajectories': return this.userSettings.trajectorySettings.trajectoryVisibility;
        case 'Air Space Boundaries': return this.userSettings.airspaceSettings;
        case 'SAAM Traffic Densities' : return this.userSettings.saamTrafficLayerSettings.saamLayerVisibility;
        case 'Route Segments' : return this.userSettings.routeSegmentVisibilities;
        case 'Annotations' : return this.userSettings.annotationsVisibility;
        case 'Flight Inspections' : return this.userSettings.flightInspectionsVisibility;
        case 'Processing Tool' : return this.userSettings.processingToolVisibility;

        //#region GRIT
        case FeatureType.Device : return this.userSettings.devicesSettings.visibility;
        case FeatureType.Receiver : return this.userSettings.receiversSettings.visibility;
        case FeatureType.InterferenceTest : return this.userSettings.interferenceTestSettings.visibility;
        case FeatureType.Radar : return this.userSettings.radarsSettings.visibility;
        //endregion
      }
    }
    createNewVisibilityLayer(type:string,id:string){
      if(!this.visibilityOfLayerIsStoredInArray(type)){
        return;
      }
      let layerVisibilities: LayerVisibility[] = this.getVisibilityObjectOfLayer(type) as  LayerVisibility[];
      let newLayerVisibility : LayerVisibility = new LayerVisibility();
      newLayerVisibility.id = id;
      newLayerVisibility.visible = true;
      layerVisibilities.push(newLayerVisibility);
      this.updateSettings();
    }
    visibilityOfLayerIsStoredInArray(type){
      return type == 'Route Segments' ||  type == 'Annotations' || type == 'Flight Inspections' || type == 'Processing Tool'
    }
    getColorObject(layerName){
      switch(layerName){
        case 'Navaids': return this.userSettings.colorSettings.navaidColors;
        case 'Coordinate Points': return this.userSettings.colorSettings.coordinatePointColors;
        case 'Air Space Boundary': return this.userSettings.colorSettings.airspaceColor;
      }
    }
    getColorObjectFromColorSettings(colorSettings:ColorSettings,layerName){
      switch(layerName){
        case 'Navaids': return colorSettings.navaidColors;
        case 'Coordinate Points': return colorSettings.coordinatePointColors;
        case 'Air Space Boundary': return colorSettings.airspaceColor;
      }
    }
    updateFilters(filtersOfType: string,newFilters: FilterModel[]){
      this.userSettings[filtersOfType] = cloneDeep(newFilters);
      this.updateSettings();
    }

    deleteStaticLayerFromSettings(layer : ConfigurationLayerModel){
      let i = this.userSettings.visibleStaticLayerIDs.findIndex(staticLayerID => layer.id == staticLayerID);
      this.userSettings.visibleStaticLayerIDs.splice(i,1);
      this.updateSettings();
    }
    addStaticLayer(layer:ConfigurationLayerModel){
      this.userSettings.visibleStaticLayerIDs.push(layer.id);
      this.updateSettings();
    }
    getCalculationTransparency(calcID: number){
      let calc = this.userSettings.calculationsTransparency.find((calc: any) => calc.id == calcID);
      if(calc == null){
        this.userSettings.calculationsTransparency.push({id:calcID, transparency: UserSettingsService.defaultTransparency});
        this.updateSettings();
        return UserSettingsService.defaultTransparency;
      }
      return calc.transparency;
    }

    createCalculationSettings(calcID:number, transparency: number,sublayers?:boolean, coverage_color?:string){
      let calcSettings;
      if(sublayers == null){
         calcSettings = {id: calcID, transparency:transparency,color: coverage_color};
      }
      else{
        calcSettings = {id:calcID, transparency:transparency,
             transparency_sub_layers: cloneDeep(UserSettingsService.default_transparency_sub_layers),color: coverage_color}
      }
      this.userSettings.calculationsTransparency.push(calcSettings);
      this.updateSettings();
      return calcSettings;
    }
    getCalculationSettings(calcID:number){
      return this.userSettings.calculationsTransparency
      .find(calculation => calculation.id ==calcID);
    }
    deleteCalculationSettings(calcID:number){
      let index = this.userSettings.calculationsVisibility.findIndex(elem => elem.id == calcID);
      if(index == -1){
        return;
      }
      this.userSettings.calculationsVisibility.splice(index,1);
      index = this.userSettings.calculationsTransparency.findIndex(elem => elem.id == calcID);
      if(index == -1){
        return;
      }
      this.userSettings.calculationsTransparency.splice(index,1);
      this.updateSettings();
    }
    deleteCalculationsSettings(calcIDs: number[]){
      calcIDs.forEach((calcID:number)=>{
        let index = this.userSettings.calculationsVisibility.findIndex(elem => elem.id == calcID);
        this.userSettings.calculationsVisibility.splice(index,1);
        index = this.userSettings.calculationsTransparency.findIndex(elem => elem.id == calcID);
        this.userSettings.calculationsTransparency.splice(index,1);
      });
      this.updateSettings();
    }
    setTransparencyCalculation(calcID:number, transparency:number,coverage_color?:string){
      let calc = this.userSettings.calculationsTransparency.find((calc: any) => calc.id == calcID);
      if(calc == null){
        if(coverage_color == null){
          this.userSettings.calculationsTransparency.push({id: calcID, transparency: transparency})
        }
        else{
          this.userSettings.calculationsTransparency.push({id: calcID, transparency: transparency, color: coverage_color})
        }
        this.updateSettings();
        return;
      }
      calc.transparency = transparency;
      if(coverage_color != null)
        calc.color = coverage_color;
      this.updateSettings();
    }
    setTransparencySubLayer(calcID:number, layerName:string, transparency:number){
      this.userSettings.calculationsTransparency
      .find((calc: any) => calc.id == calcID)
      .transparency_sub_layers[layerName] = transparency;
      this.updateSettings()
    }
    addUserLayerToUserSettings(data:string, type: string, name: string,labels:string[]){
      let maxID = 0;
      for(let userLayer of this.userSettings.userLayers){
        if(userLayer.id > maxID)
          maxID = userLayer.id;
      }
      let userLayer:UserLayer = new UserLayer(maxID+1,type,data,name,labels);
      userLayer.fillColor = UserSettingsService.defaultFillColor;
      userLayer.strokeColor= UserSettingsService.defaultLineColor;
      userLayer.lineWidth = UserSettingsService.defaultLineWidth;
      userLayer.fillOpacity = UserSettingsService.defaultStaticLayerFillOpacity;
      userLayer.lineOpacity = UserSettingsService.defaultStaticLayerLineOpacity;
      this.userSettings.userLayers.push(userLayer);
      this.updateSettings();
      return maxID+1;
    }
  addGeoJsonStaticLayer(layer: ConfigurationLayerModel, labels:string[]){
      let userLayer:UserLayer = new UserLayer(layer.id,null,null,null,labels);
      userLayer.fillColor = layer.defaultFillColor || UserSettingsService.defaultFillColor;
      userLayer.strokeColor = layer.defaultLineColor || UserSettingsService.defaultLineColor;
      userLayer.lineWidth = UserSettingsService.defaultLineWidth;
      userLayer.fillOpacity = UserSettingsService.defaultStaticLayerFillOpacity;
      userLayer.lineOpacity = UserSettingsService.defaultStaticLayerLineOpacity;

      if (layer.defaultLabelProperty) {
        userLayer.labelChecked = true;
        userLayer.propertyDisplayed = layer.defaultLabelProperty;
      }

      this.userSettings.staticGeoJsonLayers.push(userLayer);
      this.updateSettings();
    }
    getGeoJsonStaticLayer(id: number){
      return this.userSettings.staticGeoJsonLayers.find((userLayer:UserLayer) => userLayer.id == id) ;
    }
    updateTargetFields(target, source) {
      // Create a new object to hold the result, starting with the target's structure
      let result = { ...target };
      Object.keys(source).forEach(key => {
        // If the key exists in the target, update the result with the source's value
        if (key in target && typeof result[key] == typeof source[key]) {
          result[key] = source[key];
        }
        // If the key does not exist in the target, do not add it to the result
      });
      return result;
    }
    unflattenObject(flatObject) {
      const result = {};
      for (const [key, value] of Object.entries(flatObject)) {
        const parts = key.split('.');
        let current = result;
        for (let i = 0; i < parts.length; i++) {
          // If we're not at the last part, we need to either find or create the next level of the object
          if (i < parts.length - 1) {
            current[parts[i]] = current[parts[i]] || {};
            current = current[parts[i]];
          } else {
            // If we're at the last part, set the value
            current[parts[i]] = value;
          }
        }
      }
      return result;
    }
    flattenObject(obj, prefix = '', result = {}) {
      for (const [key, value] of Object.entries(obj)) {
        const flatKey = prefix ? `${prefix}.${key}` : key;
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          this.flattenObject(value, flatKey, result);
        } else {
          result[flatKey] = value;
        }
      }
      return result;
    }

}
export enum FiltersOfType{
  CoordinatePoints = 'coordinatePointFilters',
  Navaids = 'navaidFilters'
}



